import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { MdFilterList, MdAddCircle, MdVisibility } from "react-icons/md";
import loading from "../../assets/images/common/loading.gif";
import { Modal, Row, Col, Form, Button, OverlayTrigger } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import AppDataGrid from "../../components/common/AppDataGrid";

const PurchaseList = () => {
    const [items, setItems] = useState([]);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [purchaseDetails, setPurchaseDetails] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filter, setFilter] = useState({
        purchase_no: "",
        action: "FILTER"
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(baseUrl + "/inventorypurchase/", {
                action: "READ",
                school_id: userObj.school_id
            });
            setItems(response.data || []);
        } catch (error) {
            console.error("Error fetching purchases:", error);
            toast.error("Failed to fetch purchases");
        } finally {
            setIsLoading(false);
        }
    }, [baseUrl, userObj.school_id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCloseFilterModal = () => setShowFilterModal(false);
    const handleShowFilterModal = () => setShowFilterModal(true);

    const handleShowDetailModal = async (purchase) => {
        setSelectedPurchase(purchase);
        setShowDetailModal(true);
        setIsDetailsLoading(true);
        try {
            const res = await axios.get(`${baseUrl}/inventorypurchase/details/${purchase.purchase_id}`);
            setPurchaseDetails(res.data || []);
        } catch (error) {
            toast.error("Failed to fetch purchase details");
        } finally {
            setIsDetailsLoading(false);
        }
    };

    const handleFilterSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(baseUrl + "/inventorypurchase/", {
                ...filter,
                school_id: userObj.school_id,
                action: "FILTER"
            });
            setItems(response.data || []);
            setShowFilterModal(false);
        } catch (error) {
            console.error("Error filtering purchases:", error);
            toast.error("Failed to filter purchases");
        }
    };

    const handleFilterClear = () => {
        setFilter({ purchase_no: "", action: "FILTER" });
        fetchData();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("T")[0].split("-");
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
    };

    const columns = [
        {
            field: "purchase_date",
            headerName: "Date",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => <Tooltip title={formatDate(params.value)}><span>{formatDate(params.value)}</span></Tooltip>,
            sortable: true
        },
        {
            field: "purchase_no",
            headerName: "Purchase No",
            flex: 1.5,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
            sortable: true
        },
        {
            field: "remarks",
            headerName: "Remarks",
            flex: 2,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => <Tooltip title={params.value}><span>{params.value || "-"}</span></Tooltip>,
            sortable: true
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.8,
            headerAlign: "center",
            align: "center",
            sortable: false,
            renderCell: (params) => (
                <div className="tableActions">
                    <Tooltip title="View Details" arrow>
                        <span className="commonActionIcons text-primary" onClick={() => handleShowDetailModal(params.row)}>
                            <MdVisibility />
                        </span>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const filteredRecords = items.filter((item) => {
        const query = searchQuery.toLowerCase();
        return (
            (item.purchase_no || "").toLowerCase().includes(query) ||
            formatDate(item.purchase_date).toLowerCase().includes(query) ||
            (item.remarks || "").toLowerCase().includes(query)
        );
    });

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Purchases</h6>
                    </div>
                    <div className="">
                        <input
                            type="text"
                            placeholder="Search purchases..."
                            className="searchInput"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Filter</Tooltip>}>
                            <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>New Purchase</Tooltip>}>
                            <Button className="primaryBtn" variant="primary" onClick={() => navigate("/purchaseentry")}>
                                <MdAddCircle />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div className="commonTable">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={filteredRecords}
                            columns={columns}
                            getRowId={(row) => row.purchase_id}
                            pageSizeOptions={[10, 20, 50]}
                            showToolbar={false}
                            pagination
                            density="compact"
                        />
                    )}
                </div>
            </div>

            {/* Filter Modal */}
            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter Purchases</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Form onSubmit={handleFilterSubmit}>
                        <Row>
                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Purchase No</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.purchase_no}
                                            onChange={(e) => setFilter({ ...filter, purchase_no: e.target.value })}
                                            placeholder="Enter Purchase No"
                                        />
                                    </Form.Group>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleFilterClear}>
                        Reset
                    </Button>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl" centered>
                <Modal.Header closeButton style={{ backgroundColor: "#0461FE", color: "white", padding: "5px 15px" }} closeVariant="white">
                    <Modal.Title style={{ fontSize: "14px", fontWeight: "600" }}>Purchase Details - {selectedPurchase?.purchase_no}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: "0" }}>
                    {isDetailsLoading ? (
                        <div className="text-center p-4">
                            <img src={loading} alt="Loading..." style={{ width: 40 }} />
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered table-striped mb-0" style={{ border: "none" }}>
                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                    <tr>
                                        <th style={{ fontSize: "12px", padding: "8px 12px", borderTop: "none" }}>Item Name</th>
                                        <th className="text-center" style={{ fontSize: "12px", padding: "8px 12px", width: "100px", borderTop: "none" }}>Quantity</th>
                                    </tr>
                                </thead>
                                <tbody style={{ borderBottom: "none" }}>
                                    {purchaseDetails.length > 0 ? (
                                        purchaseDetails.map((detail, idx) => (
                                            <tr key={idx}>
                                                <td style={{ fontSize: "12px", padding: "6px 12px" }}>{detail.item_name}</td>
                                                <td className="text-center" style={{ fontSize: "12px", padding: "6px 12px" }}>{detail.quantity}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="text-center" style={{ fontSize: "12px", padding: "15px" }}>No details found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ padding: "5px 15px" }}>
                    <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)} style={{ fontSize: "12px" }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PurchaseList;
